/**
 * PATH: packages/ui/src/table/DirectorySheet.tsx
 * FILE_ID: UI-TABLE-DIRECTORY-SHEET-001
 * PURPOSE: The People contacts grid frame: shell, sortable headers, rows, one add row.
 * WHY: Hubs were painting their own sheets and drifting off the contacts list.
 * NON-RESPONSIBILITIES: Does not edit cells, expand rows, or pick columns.
 *   Callers own those. PeopleTable stays the contacts implementation.
 */

import { useState, type MouseEvent, type ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '../lib/utils'
import { ContextMenu, type ContextMenuItem } from '../contextMenu'
import { SortableTableHeader } from './SortableTableHeader'
import { TABLE_TD } from './tableHeaderChrome'
import type { SortDir } from './types'

/** PeopleTable shell. A pin test requires this exact string in PeopleTable.jsx. */
export const DIRECTORY_SHELL =
  'min-h-[420px] overflow-x-auto rounded-xl border border-border'

/** PeopleTable element. A pin test requires this exact string in PeopleTable.jsx. */
export const DIRECTORY_TABLE = 'w-full border-separate border-spacing-0 text-sm'

/** PeopleTable row hover. A pin test requires this exact string in PeopleTable.jsx. */
export const DIRECTORY_ROW = 'group hover:bg-accent/40'

/** PeopleTable add-row cell. A pin test requires this exact string in PeopleTable.jsx. */
export const DIRECTORY_ADD_CELL = 'border-t border-border/60 p-0'

/** PeopleTable add-row button. A pin test requires this exact string in PeopleTable.jsx. */
export const DIRECTORY_ADD_BUTTON =
  'flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary'

export type DirectoryColumn<K extends string = string> = {
  key: K
  label: string
  sortable?: boolean
  width?: string
  align?: 'left' | 'right'
}

export type DirectorySort<K extends string = string> = {
  key: K
  dir: SortDir
}

type MenuState<T> = { x: number; y: number; row: T } | null

export function DirectorySheet<T, K extends string = string>({
  label,
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  renderCell,
  onRowClick,
  menuItems,
  addLabel,
  onAdd,
  compact = false,
}: {
  label: string
  columns: DirectoryColumn<K>[]
  rows: T[]
  rowKey: (row: T) => string
  sort?: DirectorySort<K> | null
  onSort?: (key: K) => void
  renderCell: (column: DirectoryColumn<K>, row: T) => ReactNode
  onRowClick?: (row: T) => void
  menuItems?: (row: T) => ContextMenuItem[]
  addLabel?: string
  onAdd?: () => void
  /** Drop the directory page's 420px floor when the sheet sits in a drawer. */
  compact?: boolean
}) {
  const [menu, setMenu] = useState<MenuState<T>>(null)
  const showAdd = Boolean(addLabel && onAdd)

  function openMenu(event: MouseEvent, row: T) {
    event.preventDefault()
    setMenu({ x: event.clientX, y: event.clientY, row })
  }

  return (
    <div className={compact ? 'overflow-x-auto rounded-xl border border-border' : DIRECTORY_SHELL} role="region" aria-label={label} data-directory-sheet="">
      <table className={DIRECTORY_TABLE}>
        <thead>
          <tr>
            {columns.map((column) => (
              <SortableTableHeader
                key={column.key}
                label={column.label}
                sortKey={column.key}
                sortable={Boolean(column.sortable && onSort)}
                align={column.align ?? 'left'}
                activeKey={sort?.key}
                activeDir={sort?.dir}
                onSort={(key) => onSort?.(key)}
                className={column.width}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cn(DIRECTORY_ROW, onRowClick && 'cursor-pointer')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onContextMenu={menuItems ? (event) => openMenu(event, row) : undefined}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    TABLE_TD,
                    column.width,
                    column.align === 'right' && 'text-right tabular-nums',
                  )}
                >
                  {renderCell(column, row)}
                </td>
              ))}
            </tr>
          ))}
          {showAdd ? (
            <tr>
              <td colSpan={columns.length} className={DIRECTORY_ADD_CELL}>
                <button type="button" onClick={onAdd} className={DIRECTORY_ADD_BUTTON}>
                  <Plus className="h-4 w-4" /> {addLabel}
                </button>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {menu && menuItems ? (
        <ContextMenu
          open
          position={{ x: menu.x, y: menu.y }}
          items={menuItems(menu.row)}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  )
}
